import { isNaN as isNanLodash } from 'lodash';
import { ControlField, FieldMetadata } from '../contexts/MetadataContext';
import { FieldDefinition, PropertyField } from '../system/field';
import { FieldType } from '../system/field-type';

export const customValidations: any = {
  _validatePhone: (value: any) => {
    if (value && value.length < 10) {
      return {
        isValid: false,
        errorMessage: 'Phone format should be (###) ###-####',
      };
    }
    return {
      isValid: true,
    };
  },
  OFFICE_EXCLUSIVE_YN: (value: any, getFieldValue: Function) => {
    if (
      value === true &&
      getFieldValue(PropertyField.MLS_STATUS, '') === 'CLOSED-BRIGHT'
    ) {
      return {
        isValid: false,
        errorMessage:
          'If status is set to closed then Office Exclusive must be set to No and Comparable must be set to Yes',
      };
    }
    return {
      isValid: true,
    };
  },
  LIST_AGENT_MLS_ID: (value: any) => {
    if (!value) {
      return {
        isValid: false,
        errorMessage: 'No agent ID given',
      };
    }
    return {
      isValid: true,
    };
  },
  YEAR_BUILT: (value: any, getFieldValue: Function) => {
    if (
      getFieldValue('NEW_CONSTRUCTION_YN') &&
      getFieldValue('CONSTRUCTION_COMPLETED_YN')
    ) {
      const isValid = minValue(value, 1492);
      return {
        isValid,
        errorMessage: !isValid ? ErrorMessages.minValue(1492) : '',
      };
    }
    return {
      isValid: true,
    };
  },
};

const ErrorMessages = {
  maxValue: (max: number) => `The maximum value for this field is ${max}`,
  minValue: (min: number) => `The minimum value for this field is ${min}`,
  maxLength: (max: number) => `The maximum length for this field is ${max}`,
  minLength: (min: number) => `The minimum length for this field is ${min}`,
  required: () => `This field is required`,
  editMask: (mask: number) => `This field required this format ${mask}`,
  dataType: (type: number) => `This field required this type ${type}`,
};

const isNumeric = (num: any) =>
  (typeof num === 'number' || (typeof num === 'string' && num.trim() !== '')) &&
  !isNanLodash(num as number);

const editMask = (value: any, mask: RegExp) => {
  return true;
};

export const normalizeNumericValue = (value: any) => {
  let valueToCompare = typeof value === 'number' ? value : NaN;
  if (typeof value === 'string' && isNumeric(value)) {
    valueToCompare = Number(value.replace(/[^0-9.-]+/g, ''));
  } else if (typeof value === 'boolean') {
    valueToCompare = Boolean(value) === true ? 1 : 0;
  }

  return valueToCompare;
};

export const isPositive = (fieldDef: FieldDefinition) => {
  const { MinValue: minValueDef } = fieldDef;
  const minValue = normalizeNumericValue(minValueDef);
  return !Number.isNaN(minValue) && minValue >= 0;
};

const maxValue = (value: any, maxNumber: number | string) => {
  let valueToCompare = normalizeNumericValue(value);
  return valueToCompare <= Number(maxNumber);
};

const minValue = (value: any, minNumber: number | string) => {
  let valueToCompare = normalizeNumericValue(value);
  return valueToCompare >= Number(minNumber);
};

const maxLength = (
  value: any,
  length: number,
  dataType?: string,
  isSelect?: boolean,
) => {
  if (dataType === 'Boolean' || 'boolean' === typeof value) {
    return (Boolean(value) === true ? '1' : '0').length <= length;
  } else if (isSelect || Array.isArray(value)) {
    return true;
  }

  return `${value}`.length <= length;
};

const minLength = (value: any, length: number, dataType?: string) => {
  if (dataType === 'Boolean' || typeof value === 'boolean') {
    return (Boolean(value) === true ? '1' : '0').length >= length;
  } else if (dataType === 'Long' || Array.isArray(value)) {
    return (!Array.isArray(value) ? [value] : value).length >= length;
  }

  return `${value}`.length >= length;
};

const isRequired = (value: any) => {
  return value !== undefined && value !== null && value !== '';
};

const validationMap: any = {
  MinLength: {
    validationFn: minLength,
    message: ErrorMessages.minLength,
  },
  MaxLength: {
    validationFn: maxLength,
    message: ErrorMessages.maxLength,
  },
  // DataType: {
  //   validationFn: isDataType,
  //   message: ErrorMessages.dataType,
  // },
  Required: {
    validationFn: isRequired,
    message: ErrorMessages.required,
  },
  // Searchable: true,
  MinValue: {
    validationFn: minValue,
    message: ErrorMessages.minValue,
  },
  // SearchHelp: <p>A list of attributes or description of the garage.</p>,
  EditMask: {
    validationFn: editMask,
    message: ErrorMessages.editMask,
  },
  MaxValue: {
    validationFn: maxValue,
    message: ErrorMessages.maxValue,
  },
};

const shouldRunValidation = (validationValue: any) => {
  if (typeof validationValue === 'boolean' && validationValue === true) {
    return true;
  }

  return (
    validationValue !== null && validationValue !== undefined && validationValue
  );
};

const runValidation = (
  validationName: keyof FieldDefinition,
  fieldMeta: FieldMetadata,
  value: any,
) => {
  const definition = fieldMeta.Definition;
  const validationValue = definition[validationName];
  const validation = validationMap[validationName];
  if (shouldRunValidation(validationValue) && validation) {
    let errorMessage = '';
    const dataType = definition.DataType;
    const isSelect =
      fieldMeta.Lookup?.length && fieldMeta.Type === FieldType.STRING;
    if (!validation.validationFn(value, validationValue, dataType, isSelect)) {
      errorMessage = validation.message?.(validationValue) || 'Error';
    }

    return {
      validationRule: validation,
      errorMessage,
      isValid: errorMessage.length === 0,
      validationValue,
      value,
    };
  }
  return;
};

const runAllValidations = (
  field: ControlField,
  fieldMeta: FieldMetadata,
  value: any,
) => {
  const validations: any = {
    rules: {},
    errorMessages: {},
    isValid: true,
  };

  const definition = fieldMeta.Definition;
  if (definition?.Required === false && !isRequired(value)) {
    return validations;
  }

  for (const validationName in definition) {
    if (
      shouldRunValidation(definition[validationName as keyof FieldDefinition])
    ) {
      const validation = runValidation(
        validationName as keyof FieldDefinition,
        fieldMeta,
        value,
      );
      validations.rules[validationName] = validation;
      if (validation) {
        validations.isValid = validations.isValid && validation.isValid;
        if (validation.errorMessage) {
          validations.errorMessages[validationName] = validation.errorMessage;
          validations.errorMessage = validation.errorMessage;
        }
      }
    }
  }

  if (field?.Key?.includes('_PHONE') && validations.isValid) {
    const validation = customValidations._validatePhone(value);
    validations.isValid = validations.isValid && validation.isValid;
    if (validation.errorMessage) {
      validations.errorMessages['PhoneFormat'] = validation.errorMessage;
      validations.errorMessage = validation.errorMessage;
    }
  }

  return validations;
};

export const isValidFromDefinition = (
  field: ControlField,
  fieldMeta: FieldMetadata,
  value: any,
): any => {
  if (!fieldMeta.Definition) {
    // console.warn('No fieldMeta valid', field.Key);
    return { isValid: true };
  }

  return runAllValidations(field, fieldMeta, value);
};

export const runCustomValidation = (
  field: ControlField,
  value: any,
  getFieldValue: Function,
): any => {
  const customValidation = customValidations[field.Key];
  if (!customValidation) {
    return { isValid: true }; // since there are no custom validation
  }
  return customValidation(value, getFieldValue);
};
